import { useCallback, useEffect, useState } from 'react';
import { Button, Modal, ModalProps } from 'antd';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { CheckCircleFilled } from '@ant-design/icons';
import PayWithSubWindow from 'bizComponents/pay/PayWithSubWindow';
import { PriceText } from './PriceText';

export interface StripeSubscriptionModalProps extends ModalProps {
  /** Stripe 支付/签约地址（老订购接口返回的 paymentUrl） */
  paymentUrl: string;
  accountNum: number;
  packageId: Pay.PackageId;
  discountTotalPrice: string;
  currency: General.Currency;
  onRefresh: () => Promise<unknown>;
  onSubscribed?: () => void;
}

export const STRIPE_SUBSCRIPTION_RESULT_TYPE = 'STRIPE_SUBSCRIPTION_RESULT';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  > * {
    margin-bottom: 17px;
    text-align: center;
  }
`;

const Green = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--fett-scenario-success-text);
  > span {
    margin-bottom: 10px;
    font-size: 42px;
  }
`;

const Price = styled.div`
  font-size: 24px;
  color: var(--fett-colors-price);
`;

const Tips = styled.div`
  color: var(--fett-text-tertiary);
`;

const ErrorTips = styled.div`
  color: var(--fett-surface-error-text);
`;

function StripeSubscriptionModal(props: StripeSubscriptionModalProps) {
  const {
    paymentUrl,
    accountNum,
    packageId,
    discountTotalPrice,
    currency,
    onRefresh,
    onSubscribed,
    ...modalProps
  } = props;
  const { t } = useTranslate();

  // pending: 签约中；success: 订阅成功；cancel: 用户取消
  const [status, setStatus] = useState<'pending' | 'success' | 'cancel'>('pending');

  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = useCallback(() => {
    if (refreshing) {
      return Promise.resolve();
    }
    setRefreshing(true);
    return onRefresh().finally(() => setRefreshing(false));
  }, [onRefresh, refreshing]);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const { type, result } = e.data || {};
      if (type !== STRIPE_SUBSCRIPTION_RESULT_TYPE) {
        return;
      }
      if (result === 'success') {
        setStatus('success');
        onSubscribed?.();
      } else if (result === 'cancel') {
        setStatus('cancel');
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onSubscribed]);

  // 重新签约换新 paymentUrl 时重置状态
  useEffect(() => {
    setStatus('pending');
  }, [paymentUrl]);

  if (status === 'success') {
    return (
      <Modal width={480} footer={null} {...modalProps}>
        <Container>
          <Green>
            <CheckCircleFilled />
            <div>{t('translation.支付成功')}</div>
          </Green>
          <div>
            {t(`套餐.${packageId}`)} {t('market.几个帐号', { num: accountNum })}
          </div>
          <Price>
            <PriceText currency={currency} price={Number(discountTotalPrice)} format="0,0.00" />
          </Price>
        </Container>
      </Modal>
    );
  }

  return (
    <Modal width={480} footer={null} {...modalProps}>
      <Container>
        <div>
          {t(`套餐.${packageId}`)} {t('market.几个帐号', { num: accountNum })}
        </div>
        <Price>
          <PriceText currency={currency} price={Number(discountTotalPrice)} format="0,0.00" />
        </Price>
        {!paymentUrl ? (
          <>
            <ErrorTips>{t('获取签约链接失败，请重试')}</ErrorTips>
            <Button type="primary" loading={refreshing} onClick={handleRefresh}>
              {t('market.重新加载')}
            </Button>
          </>
        ) : (
          <PayWithSubWindow
            src={paymentUrl}
            name="STRIPE_SUBSCRIPTION_WINDOW"
            expired={status === 'cancel'}
            onRefresh={onRefresh}
          />
        )}
        {status === 'cancel' && <Tips>{t('market.已取消')}</Tips>}
      </Container>
    </Modal>
  );
}

export default StripeSubscriptionModal;
